import { supabase } from '../../supabaseClient';
import { notifyAdmins } from './realtime';

// ============================================================
// NOTIFICATIONS
// ============================================================
export async function getNotifications(userId) {
  const { data, error } = await supabase
    .from('notifications')
    .select('id, type, title, body, read, created_at, user_id')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw error;
  return data;
}

export async function markNotificationRead(id) {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('id', id);
  if (error) throw error;
}

export async function markAllNotificationsRead(userId) {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('user_id', userId)
    .is('read', false);
  if (error) throw error;
}

export async function getUnreadCount(userId) {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('read', false);
  if (error) throw error;
  return count;
}

// ============================================================
// FAVORITES
// ============================================================
export async function getFavorites(userId) {
  const { data, error } = await supabase
    .from('favorites')
    .select('id, driver_id, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;

  const enriched = await Promise.all(
    (data || []).map(async (fav) => {
      const { data: driverProfile } = await supabase
        .from('profiles')
        .select('name, avatar_url')
        .eq('id', fav.driver_id)
        .single();
      const { data: driverInfo } = await supabase
        .from('drivers')
        .select('rating, total_trips, vehicle_type, plate')
        .eq('driver_uid', fav.driver_id)
        .single();
      return { ...fav, profile: driverProfile, driver: driverInfo };
    })
  );
  return enriched;
}

export async function addFavorite(userId, driverId) {
  const { error } = await supabase
    .from('favorites')
    .insert({ user_id: userId, driver_id: driverId });
  if (error) throw error;
}

export async function removeFavorite(id) {
  const { error } = await supabase
    .from('favorites')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function isFavorite(userId, driverId) {
  const { data, error } = await supabase
    .from('favorites')
    .select('id')
    .eq('user_id', userId)
    .eq('driver_id', driverId)
    .maybeSingle();
  if (error) throw error;
  return !!data;
}

// ============================================================
// PAYMENT METHODS
// ============================================================
export async function getPaymentMethods(userId) {
  const { data, error } = await supabase
    .from('payment_methods')
    .select('id, user_id, type, details, is_default, created_at')
    .eq('user_id', userId)
    .order('is_default', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function savePaymentMethod(method) {
  if (method.id) {
    const { error } = await supabase
      .from('payment_methods')
      .update(method)
      .eq('id', method.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('payment_methods')
      .insert(method);
    if (error) throw error;
  }
}

export async function deletePaymentMethod(id) {
  const { error } = await supabase
    .from('payment_methods')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function setDefaultPaymentMethod(userId, id) {
  await supabase
    .from('payment_methods')
    .update({ is_default: false })
    .eq('user_id', userId);
  const { error } = await supabase
    .from('payment_methods')
    .update({ is_default: true })
    .eq('id', id);
  if (error) throw error;
}

// ============================================================
// SUPPORT TICKETS
// ============================================================
export async function getTickets(userId, isSupport) {
  let query = supabase
    .from('support_tickets')
    .select('*, messages:ticket_messages(count)')
    .order('created_at', { ascending: false });
  if (!isSupport) {
    query = query.eq('user_id', userId);
  }
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function getTicketMessages(ticketId) {
  const { data, error } = await supabase
    .from('ticket_messages')
    .select('*, user:user_id(id, name, avatar_url)')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function createTicket(ticket) {
  const { data, error } = await supabase
    .from('support_tickets')
    .insert(ticket)
    .select()
    .single();
  if (error) throw error;

  try {
    await notifyAdmins({
      type: 'support_ticket',
      title: '🎧 Nuevo Ticket de Soporte',
      body: `Asunto: ${ticket.subject || 'Consulta'} (${ticket.priority || 'Normal'})`,
      link: '/soporte'
    });
  } catch (err) {
    console.warn('Warning sending admin ticket notification:', err);
  }

  return data;
}

export async function addTicketMessage(message) {
  const { error } = await supabase
    .from('ticket_messages')
    .insert(message);
  if (error) throw error;
}

export async function updateTicketStatus(id, status) {
  const { error } = await supabase
    .from('support_tickets')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

// ============================================================
// SHIFTS
// ============================================================
const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

export { DAY_NAMES };

export async function getShifts(driverId) {
  const { data, error } = await supabase
    .from('shifts')
    .select('id, driver_id, day_of_week, start_time, end_time, is_active, shift_group_id')
    .eq('driver_id', driverId)
    .order('day_of_week')
    .order('start_time');
  if (error) throw error;
  return data || [];
}

export async function saveShift(shift) {
  if (shift.id) {
    const { error } = await supabase
      .from('shifts')
      .update(shift)
      .eq('id', shift.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('shifts')
      .insert(shift);
    if (error) throw error;
  }
}

export async function deleteShift(id) {
  const { error } = await supabase
    .from('shifts')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function toggleShift(id, isActive) {
  const { error } = await supabase
    .from('shifts')
    .update({ is_active: isActive, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function getShiftGroups() {
  const { data, error } = await supabase
    .from('shift_groups')
    .select('id, name, days_of_week, start_time, end_time')
    .order('name');
  if (error) throw error;
  return data || [];
}

export async function saveShiftGroup(group) {
  if (group.id) {
    const { error } = await supabase
      .from('shift_groups')
      .update(group)
      .eq('id', group.id);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('shift_groups')
      .insert(group);
    if (error) throw error;
  }
}

export async function deleteShiftGroup(id) {
  const { error } = await supabase
    .from('shift_groups')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function getAllDriverShifts() {
  const { data, error } = await supabase
    .from('shifts')
    .select('id, driver_id, day_of_week, start_time, end_time, is_active, shift_group_id')
    .order('day_of_week')
    .order('start_time');
  if (error) throw error;
  return data || [];
}

export async function assignShiftGroupToDriver(driverId, groupId) {
  const { data: group, error: fetchErr } = await supabase
    .from('shift_groups')
    .select('id, days_of_week, start_time, end_time')
    .eq('id', groupId)
    .single();
  if (fetchErr) throw fetchErr;

  const shiftsToInsert = group.days_of_week.map(day => ({
    driver_id: driverId,
    day_of_week: day,
    start_time: group.start_time,
    end_time: group.end_time,
    shift_group_id: group.id,
    is_active: true
  }));

  const { error: insertErr } = await supabase
    .from('shifts')
    .insert(shiftsToInsert);
  if (insertErr) throw insertErr;
}

// ============================================================

// DRIVER DOCUMENTS
// ============================================================
const DOCUMENT_LABELS = {
  cedula: 'Cédula de Identidad',
  license: 'Licencia de Conducir',
  vehicle_title: 'Título de Propiedad',
  insurance: 'Seguro',
  rif: 'RIF',
  intt_cert: 'Certificación INTT (Venezuela)',
  other: 'Otro',
};

const DOCUMENT_ICONS = {
  cedula: '🪪',
  license: '📋',
  vehicle_title: '📄',
  insurance: '🛡️',
  rif: '📑',
  intt_cert: '🏛️',
  other: '📎',
};

const REQUIRED_DOCUMENTS = [
  {
    type: 'cedula',
    label: 'Cédula de Identidad',
    verification: 'digital',
    description: 'Subir foto nítida de la cédula y/o selfie con cédula',
    sub_types: [
      { key: 'selfie_with_id', label: 'Selfie con cédula', description: 'Foto nítida del conductor sosteniendo su cédula' },
      { key: 'id_card', label: 'Foto de cédula sola', description: 'Foto legible de la cédula' },
    ],
  },
  { type: 'license', label: 'Licencia de Conducir', verification: 'digital', description: 'Licencia de conducir de 2do grado o superior vigente' },
  { type: 'intt_cert', label: 'Certificación INTT (Venezuela)', verification: 'digital', description: 'Certificación de prestación de servicio o registro emitido por el Instituto Nacional de Transporte Terrestre (INTT)' },
  { type: 'rif', label: 'Registro de Información Fiscal (RIF)', verification: 'digital', description: 'Comprobante digital del RIF actualizado del SENIAT' },
  { type: 'vehicle_title', label: 'Título / Carnet de Circulación', verification: 'digital', description: 'Carnet de circulación o título de propiedad del vehículo' },
  { type: 'insurance', label: 'Seguro / Responsabilidad Civil', verification: 'digital', description: 'Certificado de seguro o RCV obligatorio vigente' },
];

export { DOCUMENT_LABELS, DOCUMENT_ICONS, REQUIRED_DOCUMENTS };

export async function checkPendingDocuments(driverId) {
  if (!driverId) return false;
  const withTimeout = (p, ms = 3000) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))]);
  try {
    const { data, error } = await withTimeout(
      supabase
        .from('driver_documents')
        .select('id, document_type, status, expiry_date')
        .eq('driver_id', driverId)
    );
    if (error) {
      console.warn('Warning checking driver documents:', error);
      return false;
    }

    const { data: driverData } = await supabase
      .from('drivers')
      .select('status, is_approved')
      .eq('driver_uid', driverId)
      .maybeSingle();

    const isApproved = driverData && (driverData.status === 'active' || driverData.is_approved === true);

    if (!data || data.length === 0) {
      return !isApproved;
    }

    const now = new Date();
    const hasRejectedOrExpired = data.some(doc => 
      doc.status === 'rejected' ||
      (doc.expiry_date && new Date(doc.expiry_date) < now)
    );
    if (hasRejectedOrExpired) return true;

    const hasPending = data.some(doc => doc.status === 'pending');
    if (hasPending) {
      return !isApproved;
    }
    return false;
  } catch (err) {
    console.warn('Timeout or error checking driver documents:', err);
    return false;
  }
}

export async function getDriverDocumentStatusDetails(driverId) {
  if (!driverId) return { blocked: false, reason: null };
  try {
    const docs = await getDocuments(driverId);

    const { data: driverData } = await supabase
      .from('drivers')
      .select('status, is_approved')
      .eq('driver_uid', driverId)
      .maybeSingle();

    const isDriverApproved = driverData && (driverData.status === 'active' || driverData.is_approved === true);

    const now = new Date();
    const rejected = (docs || []).filter(d => d.status === 'rejected');
    if (rejected.length > 0) {
      const names = rejected.map(d => DOCUMENT_LABELS[d.document_type] || d.document_type).join(', ');
      return { blocked: true, reason: `Tienes documentos rechazados por administración: ${names}. Por favor actualízalos.` };
    }
    const expired = (docs || []).filter(d => d.expiry_date && new Date(d.expiry_date) < now);
    if (expired.length > 0) {
      const names = expired.map(d => DOCUMENT_LABELS[d.document_type] || d.document_type).join(', ');
      return { blocked: true, reason: `Tienes documentos vencidos: ${names}. Por favor carga la versión renovada.` };
    }

    if (!docs || docs.length === 0) {
      if (isDriverApproved) return { blocked: false, reason: null };
      return { blocked: true, reason: 'Debes consignar tus documentos obligatorios (Cédula, Licencia y Carnet) para activar tu disponibilidad.' };
    }

    const pending = docs.filter(d => d.status === 'pending');
    if (pending.length > 0) {
      if (isDriverApproved) return { blocked: false, reason: null };
      return { blocked: true, reason: 'Tus documentos están en proceso de validación por administración.' };
    }
    return { blocked: false, reason: null };
  } catch (err) {
    return { blocked: false, reason: null };
  }
}

export async function getDocuments(driverId) {
  if (!driverId) return [];
  try {
    const { data, error } = await supabase
      .from('driver_documents')
      .select('id, driver_id, document_type, document_url, status, notes, expiry_date, sub_type, created_at, updated_at')
      .eq('driver_id', driverId)
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('Warning getDocuments:', error);
      return [];
    }
    return data || [];
  } catch (err) {
    console.warn('Error in getDocuments:', err);
    return [];
  }
}

export async function uploadDocument(driverId, file, documentType, subType = null) {
  if (!driverId) throw new Error('No se especificó el identificador del conductor');
  if (!file) throw new Error('No se seleccionó ningún archivo');

  // 1. Validar tamaño máximo (10MB)
  const MAX_SIZE = 10 * 1024 * 1024;
  if (file.size > MAX_SIZE) {
    throw new Error('El archivo excede el tamaño máximo permitido de 10 MB');
  }

  // 2. Validar extensiones permitidas
  const fileName = file.name || 'document.jpg';
  const rawExt = fileName.split('.').pop()?.toLowerCase() || 'jpg';
  const allowedExts = ['jpg', 'jpeg', 'png', 'webp', 'pdf', 'heic', 'heif'];
  if (!allowedExts.includes(rawExt)) {
    throw new Error('Tipo de archivo no permitido. Por favor sube una imagen (JPG, PNG, WEBP) o un documento PDF.');
  }

  // 3. Determinar MIME type seguro para Supabase Storage
  let mimeType = file.type;
  if (!mimeType || mimeType === 'application/octet-stream') {
    if (rawExt === 'pdf') mimeType = 'application/pdf';
    else if (rawExt === 'png') mimeType = 'image/png';
    else if (rawExt === 'webp') mimeType = 'image/webp';
    else mimeType = 'image/jpeg';
  }

  const subTypePrefix = subType ? `${subType}_` : '';
  const filePath = `${driverId}/${documentType}_${subTypePrefix}${Date.now()}.${rawExt}`;

  // 4. Subir a Supabase Storage bucket 'driver_documents'
  const { error: uploadError } = await supabase.storage
    .from('driver_documents')
    .upload(filePath, file, { 
      contentType: mimeType,
      upsert: true 
    });

  if (uploadError) {
    console.error('Storage upload error:', uploadError);
    throw new Error(uploadError.message || 'Error al subir el archivo al almacenamiento');
  }

  // 5. Obtener URL pública
  const { data: urlData } = supabase.storage
    .from('driver_documents')
    .getPublicUrl(filePath);

  const publicUrl = urlData?.publicUrl || '';

  // 6. Verificar si ya existe documento previo del mismo tipo/subtipo para actualizar
  let query = supabase
    .from('driver_documents')
    .select('id')
    .eq('driver_id', driverId)
    .eq('document_type', documentType);

  if (subType) {
    query = query.eq('sub_type', subType);
  }

  const { data: existingDoc } = await query.maybeSingle();

  if (existingDoc?.id) {
    const { error: updateError } = await supabase
      .from('driver_documents')
      .update({
        document_url: publicUrl,
        status: 'pending',
        notes: null,
        sub_type: subType,
        updated_at: new Date().toISOString()
      })
      .eq('id', existingDoc.id);

    if (updateError) {
      console.error('Database update document error:', updateError);
      throw new Error(updateError.message || 'Error al actualizar el documento en la base de datos');
    }
  } else {
    const { error: dbError } = await supabase
      .from('driver_documents')
      .insert({
        driver_id: driverId,
        document_type: documentType,
        document_url: publicUrl,
        sub_type: subType,
        status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });

    if (dbError) {
      console.error('Database insert document error:', dbError);
      throw new Error(dbError.message || 'Error al registrar el documento en la base de datos');
    }
  }

  try {
    const docName = DOCUMENT_LABELS[documentType] || documentType;
    await notifyAdmins({
      type: 'document_uploaded',
      title: '📄 Nuevo Documento de Conductor',
      body: `Un conductor subió: ${docName}. Pendiente de revisión y validación.`,
      link: '/admin/documentos'
    });
  } catch (err) {
    console.warn('Warning sending admin doc notification:', err);
  }

  return { success: true, url: publicUrl };
}

export async function deleteDocument(id) {
  const { data: doc, error: fetchError } = await supabase
    .from('driver_documents')
    .select('document_url')
    .eq('id', id)
    .single();
  if (fetchError) throw fetchError;

  const path = doc.document_url?.split('/driver_documents/')[1];
  if (path) {
    await supabase.storage.from('driver_documents').remove([path]);
  }

  const { error } = await supabase
    .from('driver_documents')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function approveDocument(docId, reviewerId, expiryDate) {
  const payload = {
    status: 'approved',
    reviewed_by: reviewerId,
    reviewed_at: new Date().toISOString(),
  };
  if (expiryDate) payload.expiry_date = expiryDate;
  const { error } = await supabase.from('driver_documents').update(payload).eq('id', docId);
  if (error) throw error;
}

export async function rejectDocument(docId, reviewerId, notes) {
  const { error } = await supabase
    .from('driver_documents')
    .update({
      status: 'rejected',
      notes: notes || '',
      reviewed_by: reviewerId,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', docId);
  if (error) throw error;
}

export async function reopenDocument(docId) {
  const { error } = await supabase
    .from('driver_documents')
    .update({ status: 'pending', notes: null, reviewed_by: null, reviewed_at: null })
    .eq('id', docId);
  if (error) throw error;
}

export async function uploadAdminDocument(driverId, file, documentType, subType) {
  if (!driverId) throw new Error('Identificador de conductor requerido');
  if (!file) throw new Error('Archivo requerido');

  const fileName = file.name || 'document.jpg';
  const rawExt = fileName.split('.').pop()?.toLowerCase() || 'jpg';
  const allowedExts = ['jpg', 'jpeg', 'png', 'webp', 'pdf', 'heic', 'heif'];
  if (!allowedExts.includes(rawExt)) {
    throw new Error('Tipo de archivo no permitido. Sube una imagen (JPG, PNG, WEBP) o documento PDF.');
  }

  let mimeType = file.type;
  if (!mimeType || mimeType === 'application/octet-stream') {
    if (rawExt === 'pdf') mimeType = 'application/pdf';
    else if (rawExt === 'png') mimeType = 'image/png';
    else if (rawExt === 'webp') mimeType = 'image/webp';
    else mimeType = 'image/jpeg';
  }

  const filePath = `admin_uploads/${driverId}/${documentType}_${subType || 'doc'}_${Date.now()}.${rawExt}`;

  const { error: uploadError } = await supabase.storage
    .from('driver_documents')
    .upload(filePath, file, { 
      contentType: mimeType,
      upsert: true 
    });

  if (uploadError) {
    console.error('Storage admin upload error:', uploadError);
    throw new Error(uploadError.message || 'Error al subir el archivo');
  }

  const { data: urlData } = supabase.storage
    .from('driver_documents')
    .getPublicUrl(filePath);

  const publicUrl = urlData?.publicUrl || '';

  // 1. Intentar actualizar si ya existe un documento del mismo tipo
  let query = supabase.from('driver_documents').select('id').eq('driver_id', driverId).eq('document_type', documentType);
  if (subType) query = query.eq('sub_type', subType);
  const { data: existingDoc } = await query.maybeSingle();

  if (existingDoc?.id) {
    const updatePayload = {
      document_url: publicUrl,
      status: 'pending',
      updated_at: new Date().toISOString()
    };
    try {
      const { error: updErr } = await supabase.from('driver_documents').update({ ...updatePayload, sub_type: subType, verification_type: 'digital' }).eq('id', existingDoc.id);
      if (updErr) throw updErr;
    } catch (_) {
      const { error: fallbackUpdErr } = await supabase.from('driver_documents').update(updatePayload).eq('id', existingDoc.id);
      if (fallbackUpdErr) throw fallbackUpdErr;
    }
  } else {
    const insertPayload = {
      driver_id: driverId,
      document_type: documentType,
      document_url: publicUrl,
      status: 'pending',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    try {
      const { error: dbError } = await supabase.from('driver_documents').insert({ ...insertPayload, sub_type: subType, verification_type: 'digital' });
      if (dbError) throw dbError;
    } catch (_) {
      const { error: fallbackDbError } = await supabase.from('driver_documents').insert(insertPayload);
      if (fallbackDbError) {
        console.error('Database admin insert error:', fallbackDbError);
        throw new Error(fallbackDbError.message || 'Error al guardar el documento');
      }
    }
  }
}

// ============================================================
// ADMIN DRIVER STATUS MANAGEMENT
// ============================================================
export async function adminUpdateDriverStatus(driverUid, newStatus) {
  const isApproved = newStatus === 'active';

  // 1. Obtener datos actuales del perfil para no dejar name/phone en blanco
  let profileName = 'Conductor';
  let profilePhone = '';
  try {
    const { data: prof } = await supabase.from('profiles').select('name, phone, email').eq('id', driverUid).maybeSingle();
    if (prof) {
      profileName = prof.name || prof.email?.split('@')[0] || 'Conductor';
      profilePhone = prof.phone || '';
    }
  } catch (_) {}

  // 2. Intentar procedimiento almacenado SECURITY DEFINER (atómico)
  try {
    const { data, error } = await supabase.rpc('admin_update_driver_status', {
      p_driver_uid: driverUid,
      p_status: newStatus,
      p_is_approved: isApproved,
    });
    if (!error && data) {
      return data;
    }
  } catch (rpcErr) {
    console.warn('RPC admin_update_driver_status failed, using direct table update fallback:', rpcErr);
  }

  // 3. Fallback directo a tabla drivers
  const { error: drvErr } = await supabase
    .from('drivers')
    .upsert({
      driver_uid: driverUid,
      name: profileName,
      phone: profilePhone,
      status: newStatus,
      is_approved: isApproved,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'driver_uid' });

  if (drvErr) console.warn('Warning updating driver table:', drvErr);

  // 4. Actualizar tabla profiles
  const profileUpdates = {
    is_active: isApproved,
    updated_at: new Date().toISOString(),
  };
  if (newStatus === 'active') {
    profileUpdates.role = 'driver';
  }

  const { error: profErr } = await supabase
    .from('profiles')
    .update(profileUpdates)
    .eq('id', driverUid);

  if (profErr) console.warn('Warning updating profiles table:', profErr);

  // 5. Asignar rol driver en user_roles
  try {
    const { data: roleData } = await supabase.from('roles').select('id').eq('name', 'driver').maybeSingle();
    if (roleData) {
      if (newStatus === 'active') {
        await supabase
          .from('user_roles')
          .upsert({
            user_id: driverUid,
            role_id: roleData.id,
            is_active: true,
          }, { onConflict: 'user_id,role_id' });
      } else {
        await supabase
          .from('user_roles')
          .update({ is_active: false })
          .eq('user_id', driverUid)
          .eq('role_id', roleData.id);
      }
    }
  } catch (rErr) {
    console.warn('Role update fallback warning:', rErr);
  }

  return { success: true, driver_uid: driverUid, status: newStatus };
}
